import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { MatSort, Sort } from '@angular/material/sort';
import { Subject } from 'rxjs';
import { RecipeSearchContent, RecipeSearchResult } from '@shared/models/search.model';
import { MatTableDataSource } from '@angular/material/table';
import { PAGE_SIZE } from '@shared/constants/page.constant';
import { PageEvent } from '@angular/material/paginator';
import { DataRefFormat, Domain, Language, Market } from '@shared/models/dataref.model';
import { ResourceMedia } from '@shared/models/media.model';
import { ConfigService } from '@services/config.service';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { DataTableManagerComponent, DisplayData, LoadDataEvent } from '@shared/components/data-table-manager/data-table-manager.component';
import { ColumnConfiguration } from '@shared/models/generic/column-configuration.model';

@Component({
  selector: 'app-list-recipe',
  templateUrl: './list-recipe.component.html',
  styleUrls: ['./list-recipe.component.scss'],
  imports: [CommonModule, TranslateModule, DataTableManagerComponent],
})
export class ListRecipeComponent implements OnInit, OnDestroy {
  @Input() domains: Domain[] = [];
  @Input() languages: Language[] = [];
  @Input() markets: Market[] = [];
  @Input() statusesRefData: DataRefFormat[] = [];
  @Output()
    pageChange: EventEmitter<number> = new EventEmitter<number>();
  @Output()
    sortChange: EventEmitter<Sort> = new EventEmitter<Sort>();

  @ViewChild(MatSort) matSort: MatSort | null = null;

  // Propriétés pour data-table-manager
  columnConfigs: ColumnConfiguration<RecipeSearchContent>[] = [];
  tableData = new MatTableDataSource<RecipeSearchContent>([]);
  displayInfo: DisplayData = {
    resultsMessage: '',
    dataLength: 0,
    noDataMessage: 'NO_RESULT_FOR_QUERY',
    totalPages: 0,
  };
  DEFAULT_FILTER_CONFIG = {
    isFacetFilterEnabled: false,
    isFieldFilterEnabled: false,
    isSearchEnabled: false,
  };

  displayedColumns: string[] = ['domain', 'image', 'title', 'status', 'modifier', 'modificationDate', 'creator', 'creationDate'];
  dataSource: MatTableDataSource<RecipeSearchContent> = new MatTableDataSource();
  pageSize = PAGE_SIZE;
  pageIndex = 0;
  totalElements = 0;
  _sort?: Sort;
  currentLang = 'en';

  destroy$ = new Subject<void>();

  constructor(public configService: ConfigService) {
    this.createColumnConfig();
  }

  get sort(): Sort | undefined {
    return this._sort;
  }

  @Input() set searchResult(result: RecipeSearchResult | undefined | null) {
    if (result?.content && result.page) {
      this.enrichDatasource(result);
    }
  }
  ngOnInit(): void {
    this.currentLang = this.configService.getCurrentLanguage();
  }

  handlePageEvent(pageEvent: PageEvent): void {
    this.pageChange.emit(pageEvent.pageIndex);
  }

  sortData(sortEvent: Sort): void {
    this._sort = sortEvent;
    this.sortChange.emit(sortEvent);
  }

  findDomainName(key: string): string {
    const domainFind = this.domains.find((domain) => domain.key === key);
    return domainFind ? (domainFind as any)[this.currentLang] : key;
  }

  containsCover(resourceMedias: ResourceMedia[]): boolean {
    return resourceMedias?.filter((resourceMedia) => resourceMedia.isCover).length === 1;
  }

  getCover(resourceMedias: ResourceMedia[]): string {
    const resourceMedia = resourceMedias.find((resourceMedia2) => resourceMedia2.isCover);
    return resourceMedia?.media?.thumbnail ?? '';
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadData(event: LoadDataEvent): void {
    // Cette méthode sera appelée par data-table-manager
    if (event.filterParams.paginator) {
      this.pageChange.emit(event.filterParams.paginator.pageIndex);
    }
    if (event.filterParams.sort) {
      this.sortChange.emit(event.filterParams.sort);
    }
  }

  private enrichDatasource(result: RecipeSearchResult): void {
    this.dataSource = new MatTableDataSource<RecipeSearchContent>(result.content);
    this.dataSource.sort = this.matSort;
    this.totalElements = result.page.totalElements;
    this.pageIndex = result.page.number;

    // Mise à jour pour data-table-manager
    this.tableData.data = result.content;
    this.updateDisplayInfo(result.page.totalElements, result.page.totalPages);
  }

  private updateDisplayInfo(totalElements: number, totalPages: number): void {
    this.displayInfo = {
      resultsMessage: totalElements > 0 ? (totalElements === 1 ? 'RECIPE_FOUND' : 'RECIPES_FOUND') : '',
      dataLength: totalElements,
      noDataMessage: 'NO_RESULT_FOR_QUERY',
      totalPages,
    };
  }

  private createColumnConfig(): void {
    this.columnConfigs = [
      {
        dataFieldName: 'domain',
        name: 'domain',
        headerTranslationKey: 'SEARCH_DOMAIN',
        content: {
          type: 'string',
          value: (element: RecipeSearchContent) => this.findDomainName(element.domain),
        },
        sortable: true,
      },
      {
        dataFieldName: 'image',
        name: 'image',
        headerTranslationKey: 'SEARCH_IMAGE',
        content: { type: 'string', value: () => '', isImage: true },
        sortable: false,
      },
      {
        dataFieldName: 'title',
        name: 'title',
        headerTranslationKey: 'TITLE',
        content: {
          type: 'string',
          value: (element: RecipeSearchContent) => element.title,
        },
        sortable: true,
      },
      {
        dataFieldName: 'status',
        name: 'status',
        headerTranslationKey: 'SEARCH_STATUS',
        content: {
          type: 'string',
          value: (element: RecipeSearchContent) => element.status,
          isStatus: true,
        },
        sortable: true,
      },
      {
        dataFieldName: 'modifier',
        name: 'modifier',
        headerTranslationKey: 'SEARCH_MODIFIED_BY',
        content: {
          type: 'string',
          value: (element: RecipeSearchContent) => element.modifier,
        },
        sortable: true,
      },
      {
        dataFieldName: 'modificationDate',
        name: 'modificationDate',
        headerTranslationKey: 'SEARCH_MODIFICATION_DATE',
        content: {
          type: 'date',
          format: 'dd/MM/yyyy HH:mm',
        },
        sortable: true,
      },
      {
        dataFieldName: 'creator',
        name: 'creator',
        headerTranslationKey: 'SEARCH_CREATED_BY',
        content: {
          type: 'string',
          value: (element: RecipeSearchContent) => element.creator,
        },
        sortable: true,
      },
      {
        dataFieldName: 'creationDate',
        name: 'creationDate',
        headerTranslationKey: 'SEARCH_CREATION_DATE',
        content: {
          type: 'date',
          format: 'dd/MM/yyyy HH:mm',
        },
        sortable: true,
      },
    ];
  }
}
